import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config.json";
import { useCurrentUser } from "../../../../hooks/useCurrentUser";
import { getLatestFY } from "../component/answerHistoryPage/components/utils";


/* ─────────────────────────────────────────────────────────────
   Module-level cache — keyed by userId so switching accounts
   always starts fresh. Never survives a user change.
───────────────────────────────────────────────────────────── */
const _kpiCache = {
  userId: null,
  kpiList: null,
  financialYears: null,
  locations: null,
  frameworks: null,
  unitSettings: null,
  selectedFY: null,
  analyticsKey: null,
};

const _clearCache = () => {
  _kpiCache.userId = null;
  _kpiCache.kpiList = null;
  _kpiCache.financialYears = null;
  _kpiCache.locations = null;
  _kpiCache.frameworks = null;
  _kpiCache.unitSettings = null;
  _kpiCache.selectedFY = null;
  _kpiCache.analyticsKey = null;
};

export const invalidateKpiCache = _clearCache;


/* ─────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────── */
const buildAssignedMap = (assignedDetail = []) => {
  const map = {};
  for (const detail of assignedDetail) {
    const qid = String(detail.questionId);
    if (!map[qid]) {
      map[qid] = { assigned_to: [], assigned_by: [], due_date: null, raw: null, raw_list: [] };
    }
    map[qid].raw_list.push(detail);

    for (const u of detail.assignedToDetails || []) {
      if (!map[qid].assigned_to.some((x) => x.id === u.id)) {
        map[qid].assigned_to.push({
          id: u.id,
          first_name: u.first_name || "",
          last_name: u.last_name || "",
          email: u.email || "",
          designation: u.designation || "",
          name: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email,
        });
      }
    }
    for (const b of detail.assignedByDetails || []) {
      if (!map[qid].assigned_by.some((x) => x.id === b.id)) {
        map[qid].assigned_by.push({
          id: b.id,
          first_name: b.first_name || "",
          last_name: b.last_name || "",
          email: b.email || "",
          name: `${b.first_name || ""} ${b.last_name || ""}`.trim() || b.email,
        });
      }
    }
    if (detail.dueDate && (!map[qid].due_date || new Date(detail.dueDate) < new Date(map[qid].due_date))) {
      map[qid].due_date = detail.dueDate;
    }
    map[qid].raw = detail;
  }
  return map;
};

const groupQuantativeByQuestion = (rows = []) => {
  const map = {};
  for (const r of rows) {
    const qid = String(r.questionId);
    if (!map[qid]) map[qid] = [];
    map[qid].push(r);
  }
  return map;
};

const parseFrameworkIds = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch { return []; }
  }
  return [];
};



const parseTags = (raw) => {
  /* ── already in tree format ── */
  if (raw && typeof raw === "object" && !Array.isArray(raw) && raw) {
    return raw;
  }

  let pairs = [];

  if (!raw) return null;

  if (Array.isArray(raw)) {
    pairs = raw;
  } else if (typeof raw === "string") {
    try {
      pairs = JSON.parse(raw);
    } catch {
      return null;
    }
  }

  if (!Array.isArray(pairs) || pairs.length === 0) return null;

  /* ── Detect format ── */

  // Format 3: hierarchical array — each item has ONLY `key` (no `value`),
  // or items without a `value` field appear at index > 0.
  // Heuristic: if the first item has no `value` (or empty `value`) and
  // there are multiple entries, treat as a depth chain.
  const isHierarchical =
    pairs.length > 1 &&
    pairs.some((p, i) => i > 0 && !p.value && p.key);

  if (isHierarchical) {
    // Build a single branch: root → child → grandchild → …
    // Each entry at index i is a child of the entry at index i-1.
    const root = [];
    let currentChildren = root;
    let parentKey = "";

    pairs.forEach((item, idx) => {
      if (!item.key) return;

      const nodeKey = idx === 0 ? item.key : `${parentKey}::${item.key}`;
      const node = { key: nodeKey, label: item.key, children: [] };

      currentChildren.push(node);

      // If there's a leaf value, attach it as a child
      if (item.value) {
        const leafKey = `${nodeKey}::${item.value}`;
        node.children.push({ key: leafKey, label: item.value, children: [] });
      }

      parentKey = nodeKey;
      currentChildren = node.children;
    });

    return root.length > 0 ? { options: root } : null;
  }

  /* ── Format 2: flat key-value pairs ── */
  // Group by `key` → parent; `value` → leaf child.
  const groupMap = {};

  for (const item of pairs) {
    const { key, value } = item;
    if (!key) continue;

    if (!groupMap[key]) {
      groupMap[key] = { key, label: key, children: [] };
    }

    if (value) {
      const childKey = `${key}::${value}`;
      if (!groupMap[key].children.some((c) => c.key === childKey)) {
        groupMap[key].children.push({ key: childKey, label: value, children: [] });
      }
    }
  }

  const options = Object.values(groupMap);
  return options.length > 0 ? { options } : null;
};



export const useKpiData = ({
  financialYearId: externalFyId = null,
  answerFilters = {},
  emissionMode
} = {}) => {
  const { allSources, currentUserId } = useCurrentUser();

  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState(null);
  const [locations, setLocations] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [kpiList, setKpiList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [answeredQids, setAnsweredQids] = useState(new Set()); // ← NEW

  const modules = [...new Set(kpiList.map((k) => k.module).filter(Boolean))];

  const fyListRef = useRef([]);
  const lastAnalyticsKey = useRef(null);
  const loadedForUser = useRef(null);

  /* ── static fetchers ── */
  const fetchFinancialYears = async () => {
    const { isSuccess, data } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getFinancialYear`, {}, {}, "GET");
    return isSuccess ? data?.data || [] : [];
  };

  const fetchLocations = async () => {
    const { isSuccess, data } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getSource`, {}, {}, "GET");
    return isSuccess
      ? data?.data?.map((l) => ({
        id: String(l.id),
        name: l.unitCode || (l?.location?.area + ' ' + l.location?.city + ' ' + l.location?.state + ' ' + l.location?.country + ' ' + l.location?.zipCode),
        location: l,
      })) || []
      : [];
  };

  const fetchFrameworks = async () => {
    const userId = JSON.parse(localStorage.getItem("user_temp_id"));
    const { isSuccess, data } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getFramework`, {}, { type: "ALL", userId }, "GET");
    return isSuccess ? data?.data || [] : [];
  };


  const fetchUnitSettings = async () => {
    const { isSuccess, data } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getUnitKpiSettings`, {}, {}, "GET");

    if (!isSuccess || !data?.data) return {};
    const map = {};
    data.data.forEach((u) => {
      if (u.hasUnits) {
        map[String(u.kpiId)] = u;
      }
    });
    return map;
  };

  const fetchQuestions = async (fyId, fwIds) => {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestionnew`,
      {},
      {
        financialYearId: fyId,
        frameworkIds: fwIds,
        question_type_filter: JSON.stringify(['quantitative']),
      },
      "GET"
    );
    const questions = isSuccess ? data?.data || [] : [];
    const assignedDetail =
      Array.isArray(data?.assignedDetail) ? data.assignedDetail :
        Array.isArray(data?.assigned_details) ? data.assigned_details :
          questions.map((q) => q.assignedDetail || []).flat();
    return { questions, assignedMap: buildAssignedMap(assignedDetail) };
  };

  const fetchQuantativeAnswers = async (fyId) => {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}quantative-data`,
      {},
      { financial_year_id: fyId },
      "GET"
    );
    return isSuccess ? data || [] : [];
  };

  /* ── analytics ── */
  const fetchAnalytics = useCallback(async (fyObj, filters = {}) => {
    if (!fyObj) return [];
    const fyList = fyListRef.current;
    let previousFyId = null;
    if (fyObj.financial_year_value) {
      const [start, end] = fyObj.financial_year_value.split("-").map(Number);
      const prevValue = `${start - 1}-${end - 1}`;
      const prevObj = fyList.find((f) => f.financial_year_value === prevValue);
      previousFyId = prevObj?.id || null;
    }
    const requestData = {
      financialYearId: fyObj.id,
      previousFinancialYearId: previousFyId,
      ...(filters.location_ids?.length
        ? { locationIds: filters.location_ids }
        : { locationIds: allSources }),
      ...(filters.from_date ? { fromDate: filters.from_date } : {}),
      ...(filters.to_date ? { toDate: filters.to_date } : {}),
    };
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/optimized-analytics`,
      {},
      requestData,
      "GET"
    );
    return isSuccess ? data : [];
  }, [allSources]);

  const applyAnalytics = useCallback((analyticsRows) => {
    const analyticsMap = {};
    const qids = new Set(); // ← NEW

    (analyticsRows || []).forEach((a) => {
      analyticsMap[String(a.qid)] = a;
      qids.add(String(a.qid)); // ← NEW
    });

    setAnsweredQids(qids); // ← NEW

    setKpiList((prev) => {
      const next = prev.map((kpi) => {
        const a = analyticsMap[String(kpi.id)] || null;
        return {
          ...kpi,
          analytics: a,
          // general
          value: a?.fy_value ?? "-",
          unit: a?.unit,
          // emission
          fy_emission: a?.fy_emission ?? "-",
          previous_fy_emission: a?.previous_fy_emission ?? "-",
          emission_unit: a?.emission_unit ?? "",
          fy_energy: a?.fy_energy ?? "-",
          previous_fy_energy: a?.previous_fy_energy ?? "-",
          energy_unit: a?.energy_unit ?? "",
        };
      });
      _kpiCache.kpiList = next;
      return next;
    });
  }, []);

  /* ── user-change cache wipe ── */
  useEffect(() => {
    if (!currentUserId) return;
    if (_kpiCache.userId && _kpiCache.userId !== String(currentUserId)) {
      _clearCache();
      lastAnalyticsKey.current = null;
      loadedForUser.current = null;
    }
  }, [currentUserId]);

  /* ── Helper: map a question object → kpi row ── */

  const mapQuestionToKpi = (q, fy, unitSettingsMap, assignedMap, quantativeMap, analyticsMap = {}) => {
    const qid = String(q.questionId);
    const unitSetting = unitSettingsMap[qid] || null;
    const a = analyticsMap[qid] || null;

    return {
      id: q.questionId,
      financial_year_id: fy.id,
      module: q.moduleName || q.module || "—",
      kpi_name: q.title || q.kpi_name || "—",
      frequency: q.frequency || "—",
      framework: q.frameworkName || q.framework || "",
      mapFrameworkIds: parseFrameworkIds(q.mapFrameworkIds),
      location_id: q.location_id ?? null,
      answered_date: q.answered_date ?? null,
      from_date: q.from_date ?? null,
      to_date: q.to_date ?? null,
      // general
      value: a?.fy_value ?? null,
      unit: a?.unit ?? null,
      // emission
      fy_emission: a?.fy_emission ?? null,
      previous_fy_emission: a?.previous_fy_emission ?? null,
      emission_unit: a?.emission_unit ?? null,
      // energy
      fy_energy: a?.fy_energy ?? null,
      previous_fy_energy: a?.previous_fy_energy ?? null,
      energy_unit: a?.energy_unit ?? null,
      unitSetting: unitSetting || null,
      quantative_history: quantativeMap[qid] || [],
      assigned_to: assignedMap[qid]?.assigned_to || [],
      assigned_by: assignedMap[qid]?.assigned_by || [],
      due_date: assignedMap[qid]?.due_date || null,
      assign_raw: assignedMap[qid]?.raw || null,
      assign_raw_list: assignedMap[qid]?.raw_list || [],
      analytics: a,
      kpi_type: q.kpi_type,
      rawTags: q.tags,
      tags: parseTags(q.tags),
    };
  };

  useEffect(() => {
    if (!selectedFY?.id || !frameworks.length) return;

    let cancelled = false;

    (async () => {
      setIsLoading(true);

      try {
        const unitSettingsMap = _kpiCache.unitSettings || {};

        const [{ questions, assignedMap }, quantativeRows] = await Promise.all([
          fetchQuestions(selectedFY.id, frameworks.map((f) => f.id)),
          fetchQuantativeAnswers(selectedFY.id),
        ]);

        const quantativeMap = groupQuantativeByQuestion(quantativeRows);

        const baseKpis = questions.map((q) =>
          mapQuestionToKpi(q, selectedFY, unitSettingsMap, assignedMap, quantativeMap)
        );

        if (!cancelled) {
          setKpiList(baseKpis);
        }

        // fetch analytics after base data
        const analyticsRows = await fetchAnalytics(selectedFY, answerFilters);

        if (!cancelled) {
          applyAnalytics(analyticsRows);
        }

      } catch (e) {
        if (!cancelled) setError("Failed to reload data for selected FY");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true };
  }, [selectedFY?.id]);

  useEffect(() => {
    if (!currentUserId) return;

    let cancelled = false;

    (async () => {
      const cacheValid =
        _kpiCache.kpiList &&
        _kpiCache.userId === String(currentUserId);

      if (cacheValid) {
        if (!cancelled) {
          setKpiList(_kpiCache.kpiList);
          setFinancialYears(_kpiCache.financialYears);
          setLocations(_kpiCache.locations);
          setFrameworks(_kpiCache.frameworks);
          setSelectedFY(_kpiCache.selectedFY);
          fyListRef.current = _kpiCache.financialYears;
          lastAnalyticsKey.current = _kpiCache.analyticsKey;
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const [fyList, locs, fw, unitSettingsMap] = await Promise.all([
          fetchFinancialYears(),
          fetchLocations(),
          fetchFrameworks(),
          fetchUnitSettings(),
        ]);

        fyListRef.current = fyList;
        _kpiCache.userId = String(currentUserId);
        _kpiCache.financialYears = fyList;
        _kpiCache.locations = locs;
        _kpiCache.frameworks = fw;
        _kpiCache.unitSettings = unitSettingsMap;

        if (!cancelled) {
          setFinancialYears(fyList);
          setLocations(locs);
          setFrameworks(fw);
        }

        if (!fw.length) { setIsLoading(false); return; }

        const fy = externalFyId
          ? fyList.find((f) => f.id === externalFyId)
          : getLatestFY(fyList);

        if (!fy) { setIsLoading(false); return; }

        const fyObj = { id: fy.id, name: fy.financial_year_value, financial_year_value: fy.financial_year_value };
        _kpiCache.selectedFY = fyObj;
        if (!cancelled) setSelectedFY(fyObj);

        const [{ questions, assignedMap }, quantativeRows] = await Promise.all([
          fetchQuestions(fy.id, fw.map((f) => f.id)),
          fetchQuantativeAnswers(fy.id),
        ]);

        const quantativeMap = groupQuantativeByQuestion(quantativeRows);

        const baseKpis = questions.map((q) =>
          mapQuestionToKpi(q, fyObj, unitSettingsMap, assignedMap, quantativeMap)
        );

        if (!cancelled) setKpiList(baseKpis);

        const analyticsRows = await fetchAnalytics(fyObj, answerFilters);
        const analyticsKey = [
          fy.id,
          (answerFilters?.location_ids || []).join(","),
          answerFilters?.from_date || "",
          answerFilters?.to_date || "",
          answerFilters?.unit || "",
        ].join("|");

        lastAnalyticsKey.current = analyticsKey;
        _kpiCache.analyticsKey = analyticsKey;
        loadedForUser.current = String(currentUserId);

        if (!cancelled) applyAnalytics(analyticsRows);
      } catch {
        if (!cancelled) setError("Failed to load KPI data");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, externalFyId]);

  /* ── analytics re-fetch on filter change ── */
  useEffect(() => {
    if (!selectedFY?.id) return;
    if (loadedForUser.current !== String(currentUserId)) return;

    const key = [
      selectedFY.id,
      JSON.stringify(answerFilters?.location_ids || []),
      answerFilters?.from_date || "",
      answerFilters?.to_date || "",
      answerFilters?.unit || "",
    ].join("|");

    if (lastAnalyticsKey.current === key) return;
    lastAnalyticsKey.current = key;

    let cancelled = false;
    const filtersSnapshot = { ...answerFilters };
    const fySnapshot = { ...selectedFY };

    (async () => {
      const analyticsRows = await fetchAnalytics(fySnapshot, filtersSnapshot);
      if (!cancelled) applyAnalytics(analyticsRows);
    })();

    return () => { cancelled = true; };
  }, [
    selectedFY?.id,
    JSON.stringify(answerFilters?.location_ids),
    answerFilters?.from_date,
    answerFilters?.to_date,
    answerFilters?.unit,
    currentUserId,
  ]);

  /* ── saveAnswer ── */
  const saveAnswer = useCallback(async (payload) => {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}quantative-data`,
      {},
      payload,
      "POST"
    );
    return { isSuccess, data };
  }, []);

  /* ── refetch ── */
  const refetch = useCallback(async () => {
    if (!selectedFY || !frameworks.length) return;

    const unitSettingsMap = _kpiCache.unitSettings || {};

    const [{ questions, assignedMap }, quantativeRows, analyticsRows] = await Promise.all([
      fetchQuestions(selectedFY.id, frameworks.map((f) => f.id)),
      fetchQuantativeAnswers(selectedFY.id),
      fetchAnalytics(selectedFY, answerFilters),
    ]);

    const analyticsMap = {};
    const quantativeMap = groupQuantativeByQuestion(quantativeRows);
    (analyticsRows || []).forEach((a) => { analyticsMap[String(a.qid)] = a; });

    const updatedKpis = questions.map((q) =>
      mapQuestionToKpi(q, selectedFY, unitSettingsMap, assignedMap, quantativeMap, analyticsMap)
    );

    setKpiList(updatedKpis);
    _kpiCache.kpiList = updatedKpis;
    _kpiCache.selectedFY = selectedFY;
    _kpiCache.userId = String(currentUserId);

    const analyticsKey = [
      selectedFY.id,
      answerFilters?.location_ids || "",
      answerFilters?.from_date || "",
      answerFilters?.to_date || "",
      answerFilters?.unit || "",
    ].join("|");

    _kpiCache.analyticsKey = analyticsKey;
    lastAnalyticsKey.current = analyticsKey;

    // ← NEW: also refresh answeredQids on refetch
    const qids = new Set();
    (analyticsRows || []).forEach((a) => { qids.add(String(a.qid)); });
    setAnsweredQids(qids);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFY, frameworks, answerFilters, fetchAnalytics, currentUserId]);

  /* ── setSelectedFY — keep cache in sync ── */
  const setSelectedFYAndCache = useCallback((fy) => {
    setSelectedFY(fy);
    if (fy) _kpiCache.selectedFY = fy;
  }, []);

  return {
    kpiList,
    isLoading,
    error,
    financialYears,
    locations,
    modules,
    frameworks,
    selectedFY,
    setSelectedFY: setSelectedFYAndCache,
    saveAnswer,
    refetch,
    invalidateKpiCache,
    answeredQids, // ← NEW
  };
};